import { ZERO_WEI } from '@kwenta/sdk/constants'
import { PerpsProvider } from '@kwenta/sdk/types'
import { formatDollars, formatPercent } from '@kwenta/sdk/utils'
import type Wei from '@kwenta/wei'
import { memo, useMemo, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import HelpIcon from 'assets/svg/app/question-mark.svg'
import { InfoBoxRow } from 'components/InfoBox'
import { Body } from 'components/Text'
import Tooltip from 'components/Tooltip/TextTooltip'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { NO_VALUE } from 'constants/placeholder'
import { FEEREBATE_DISABLED } from 'constants/ui'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { useAppSelector } from 'state/hooks'

const ExecutionFeeTooltip = memo(() => {
	const { t } = useTranslation()

	return (
		<Tooltip
			height="auto"
			preset="top"
			width="240px"
			content={t('futures.market.trade.fees.execution-fees-tooltip')}
			style={{ textTransform: 'none', left: '30px' }}
		>
			<StyledHelpIcon />
		</Tooltip>
	)
})

const ExecutionFeeRow = memo(({ executionFee }: { executionFee?: Wei }) => {
	const { t } = useTranslation()
	return (
		<InfoBoxRow
			title={t('futures.market.trade.fees.execution-fees')}
			textValue={
				executionFee
					? formatDollars(executionFee, { suggestDecimals: true, maxDecimals: 3 })
					: NO_VALUE
			}
			keyNode={<ExecutionFeeTooltip />}
			isSubItem
		/>
	)
})

const FeeRebateTooltip = memo(() => {
	const { t } = useTranslation()

	return (
		<Tooltip
			height="auto"
			preset="top"
			width="240px"
			content={t('futures.market.trade.fees.fee-rebate-tooltip')}
			style={{ textTransform: 'none', left: '30px' }}
		>
			<StyledHelpIcon />
		</Tooltip>
	)
})

const FeeRebateRow = memo(({ feeRebate }: { feeRebate?: Wei }) => {
	const { t } = useTranslation()
	return (
		<InfoBoxRow
			color="secondary"
			title={t('futures.market.trade.fees.est-rebate')}
			textValue={
				feeRebate ? formatDollars(feeRebate, { suggestDecimals: true, maxDecimals: 3 }) : NO_VALUE
			}
			keyNode={<FeeRebateTooltip />}
			isSubItem
		/>
	)
})

const EstimatedTradeFeeRow = memo(({ rates, tradeFee }: { rates?: FeeRates; tradeFee?: Wei }) => {
	const { t } = useTranslation()
	const title = rates
		? t('futures.market.trade.fees.maker-taker-fees-with-rates', {
				maker: formatPercent(rates.maker, { minDecimals: 3 }).slice(1),
				taker: formatPercent(rates.taker, { minDecimals: 3 }).slice(1),
			})
		: t('futures.market.trade.fees.maker-taker-fees')

	return (
		<InfoBoxRow
			title={title}
			textValue={
				tradeFee ? formatDollars(tradeFee, { suggestDecimals: true, maxDecimals: 3 }) : NO_VALUE
			}
			keyNode={<MarketCostTooltip />}
			isSubItem
		/>
	)
})

const MarketCostTooltip = memo(() => {
	const { t } = useTranslation()

	return (
		<Tooltip
			height="auto"
			preset="top"
			width="250px"
			content={t('futures.market.trade.fees.tooltip')}
			style={{ textTransform: 'none', left: '-10px' }}
		>
			<StyledHelpIcon />
		</Tooltip>
	)
})

type FeeRates = {
	maker: Wei
	taker: Wei
}

type FeesRowProps = {
	tradeFee?: Wei
	executionFee?: Wei
	rates: FeeRates
}

export const DiscountedFeeRow = memo(
	({
		tradeFee,
		totalFee,
		showFeeRebate,
	}: {
		tradeFee: Wei | undefined
		totalFee: Wei | string
		showFeeRebate: boolean
	}) => {
		return (
			<FeeRebateContainer justifyContent="flex-end" columnGap="5px">
				{tradeFee && (
					<StrikeThroughText color="primary" mono $isFeeRebate={showFeeRebate}>
						{formatDollars(totalFee, { suggestDecimals: true, maxDecimals: 3 })}
					</StrikeThroughText>
				)}
				{showFeeRebate && tradeFee && (
					<Body color="preview" mono>
						{formatDollars(totalFee.sub(tradeFee.mul(0.9)), {
							suggestDecimals: true,
							maxDecimals: 3,
						})}
					</Body>
				)}
			</FeeRebateContainer>
		)
	}
)

const FeeRows = memo(({ tradeFee, executionFee, rates }: FeesRowProps) => {
	const { t } = useTranslation()
	const [expanded, toggleExpanded] = useReducer((s) => !s, false)
	const provider = useAppSelector(selectPerpsProvider)

	const totalFee = useMemo(() => {
		if (!tradeFee) return NO_VALUE
		return tradeFee.add(executionFee ?? ZERO_WEI)
	}, [tradeFee, executionFee])

	const showFeeRebate = useMemo(
		() =>
			!!tradeFee &&
			tradeFee.gt(0.0001) &&
			provider === PerpsProvider.SNX_V3_BASE &&
			!FEEREBATE_DISABLED,
		[provider, tradeFee]
	)

	return (
		<InfoBoxRow
			title={t('futures.market.trade.fees.total-cost')}
			textValue={
				tradeFee ? formatDollars(totalFee, { suggestDecimals: true, maxDecimals: 3 }) : NO_VALUE
			}
			nodeValue={
				showFeeRebate ? (
					<DiscountedFeeRow tradeFee={tradeFee} totalFee={totalFee} showFeeRebate={showFeeRebate} />
				) : null
			}
			expandable
			expanded={expanded}
			onToggleExpand={toggleExpanded}
		>
			<FeeRowsContainer $expanded={expanded}>
				<EstimatedTradeFeeRow rates={rates} tradeFee={tradeFee} />
				{showFeeRebate && <FeeRebateRow feeRebate={tradeFee?.mul(0.9)} />}
				<ExecutionFeeRow executionFee={executionFee} />
			</FeeRowsContainer>
		</InfoBoxRow>
	)
})

const FeeRebateContainer = styled(FlexDivRowCentered)`
	padding-right: 1px;
`

const FeeRowsContainer = styled(FlexDivCol)<{ $expanded: boolean }>`
	padding-left: 10px;
	margin-bottom: ${(props) => (props.$expanded ? '5px' : '0')};
	border-left: ${(props) =>
		props.$expanded && `2px solid ${props.theme.colors.selectedTheme.newTheme.border.color}`};
`

const StrikeThroughText = styled(Body)<{ $isFeeRebate: boolean }>`
	text-decoration: ${(props) => (props.$isFeeRebate ? 'line-through' : 'initial')};
`

export default FeeRows

const StyledHelpIcon = styled(HelpIcon)`
	margin-left: 4px;
`
